import { s } from "../shapes"
import { HttpEndpoint } from "./endpoint"
import { Endpoint } from "./endpoint2";
import { Http } from "./http"
import { NetworkClient } from "./web";
import { WebSocket } from "./websocket";

export type HttpApi = HttpApi.Def;
export namespace HttpApi {
  /**
   * A Http API is a tree of path segments
   * that terminate in Endpoints.
   * 
   * An Api node represents a section of that tree,
   * which is either a further subtree (branch) of paths,
   * or an Endpoint.
   * 
   * Api Nodes sit at the end of a PathMap
   */
  export type ApiNode =
    | Branch
    | HttpEndpoint
    | Methods

  /**
   * A map of Path segments and APINodes. If an ApiNode is
   * a Branch (aka something that contains another path node),
   * the final path of the endpoint is all the paths
   * (From Root/Def to Endpoint) concatenated together with '/'
   */
  export type PathMap = { [Path in string]: ApiNode }

  /**
   * An ApiNode that is just more API.
   */
  export type Branch = {
    type: 'branch',
    paths: PathMap
  }
  /**
   * The "Root" to an API.
   * 
   * It is important to declare a specific node a root - as
   * if you attempt to make a HTTP requests against a partial path:
   * missing the root, you will not succeed.
   * 
   * As such, clients should only accept a "Root"/"Def" element,
   * and never just a Branch element.
   */
  export type Def = {
    type: 'api',
    paths: PathMap
  }

  export type Methods = {
    type: 'methods',
    methods: {
      [Method in HttpEndpoint.Method]?: Endpoint
    }
  }

  // TODO: upgrade this to actually handle endpoint URLs (since different methods require different params)
  export type URLMap<T extends ApiNode | Def> = T extends { type: 'branch' | 'api' }
    ? { [Path in keyof T["paths"]]: URLMap<T["paths"][Path]> }
    : URL 

  /**
   * For every endpoint on an ApiNode (or Root), describe either the
   * server handler or client handler for the endpoint),
   * or describe a child object for a Branch that recurses through this type.
   */
  export type Handler<H extends "server" | "client", T extends ApiNode | Def = ApiNode> =
    T extends (Branch | Def) ? { [Path in keyof T["paths"]]: Handler<H, T["paths"][Path]> }
    : T extends HttpEndpoint ?
      (H extends "server" ? HttpEndpoint.ServerHandler<T> : HttpEndpoint.Client<T>)
    : T extends Methods ?
      { [Method in keyof T["methods"]]: Endpoint.Implementation<Extract<T["methods"][Method], Endpoint>>[H] }
    : never;

  export type Client<T extends ApiNode | Def> = Handler<"client", T>;

  /**
   * A utility type to check if a particular type of HttpEndpoint
   * is present inside an API.
   * 
   * Can be used to type-safe require certain clients or arguments
   */
  export type Includes<T extends ApiNode | Def, Subject extends HttpEndpoint> =
    T extends Subject ? true
    : T extends HttpEndpoint ? false
    : (T extends (Branch | Def) ? {
      [Path in keyof T["paths"]]: Includes<T["paths"][Path], Subject>
    }[keyof T["paths"]] : never) extends false ? false : true

  export type Route =
    | { type: 'http', http: Http.Route, endpoint: HttpEndpoint }
    | { type: 'websocket', websocket: WebSocket.Route, endpoint: HttpEndpoint.WebSocket }
}

type IncludesHttp<T extends HttpApi> = HttpApi.Includes<T, HttpEndpoint.Json | HttpEndpoint.Raw>;
type IncludesWS<T extends HttpApi> = HttpApi.Includes<T, HttpEndpoint.WebSocket>;

type RequiredClients<T extends HttpApi> = true extends IncludesHttp<T> & IncludesWS<T> ? { http: Http.Client, websocket: WebSocket.Client }
  : IncludesHttp<T> extends true ? { http: Http.Client }
  : IncludesWS<T> extends true ? { websocket: WebSocket.Client }
  : {}

export const HttpApi = {
  // Definitions
  Root<T extends HttpApi.PathMap>(paths: T) {
    return { type: 'api', paths } as const;
  },
  Branch<T extends HttpApi.PathMap>(paths: T) {
    return { type: 'branch', paths } as const;
  },
  ServerHandler<T extends HttpApi.ApiNode>(_: T, handler: HttpApi.Handler<"server", T>) {
    return handler;
  },
  Methods<T extends HttpApi.Methods["methods"]>(methods: T) {
    return { type: 'methods', methods } satisfies HttpApi.Methods;
  },

  // Constructors
  createRoutes<T extends HttpApi.Def>(def: T, handlers: HttpApi.Handler<"server", T>): HttpApi.Route[] {
    const createRoutesForNode = (
      pathParts: string[],
      node: HttpApi.ApiNode,
      handler: HttpApi.Handler<"server">,
    ): HttpApi.Route[] => {
      switch (node.type) {
        case 'branch':
          return createRoutesForPaths(pathParts, node.paths,
            handler as HttpApi.Handler<"server", HttpApi.Branch>);
        case 'raw':
          return [{ type: 'http', endpoint: node, http:
            HttpEndpoint.Routes.Raw(pathParts.join('/'), node,
              handler as HttpEndpoint.Raw.ServerHandler)
          }];
        case 'websocket':
          return [{
            type: 'websocket', endpoint: node, websocket:
            HttpEndpoint.Routes.WebSocket(pathParts.join('/'), node,
              handler as HttpEndpoint.WebSocket.ServerHandler<HttpEndpoint.WebSocket>),
          }]
        case 'json':
          return HttpEndpoint.Routes.Json(pathParts.join('/'), node,
            handler as HttpEndpoint.Json.ServerHandler).map(http => ({ type: 'http', http, endpoint: node }))
        case 'rest':
          return [];
        case 'methods':
          return Object.entries(node.methods).map(([method, methodDef]) => {
            const methodHandler = handler as HttpApi.Handler<"server", HttpApi.Methods>;
            return Endpoint.server(methodDef, pathParts.join('/'), method, (methodHandler as any)[method])
          })
      }
    }

    const createRoutesForPaths = (
      parentParts: string[],
      paths: HttpApi.PathMap,
      pathHandlers: HttpApi.Handler<"server", HttpApi.Branch>,
    ): HttpApi.Route[] => {
      return Object.entries(paths)
        .map(([path, value]) => {
          const pathParts = [...parentParts, ...path.split('/')]
            .filter(Boolean)
          return createRoutesForNode(pathParts, value, pathHandlers[path]);
        })
        .flat(1)
    }

    return createRoutesForPaths([], def.paths, handlers);
  },

  createHandlers<T extends HttpApi.Def>(def: T, routes: HttpApi.Route[]): {
    http: IncludesHttp<T> extends true ? Http.Handler : null,
    ws: IncludesWS<T> extends true ? WebSocket.Handler : null,
    wsProtocols: IncludesWS<T> extends true ? WebSocket.ProtocolNegotiation.Handler : null,
  } {

    const httpRoutes = routes.filter(x => x.type === 'http')
    const wsRoutes = routes.filter(x => x.type === 'websocket').map(x => x.websocket);

    const endpointByPath = new Map(httpRoutes.map(route => [route.http.path, route.endpoint]));

    const uniquePaths = [...new Set(httpRoutes.map(route => route.http.path))];
    const methodsByPath = new Map(uniquePaths.map(path => {
      return [path, httpRoutes.filter(route => route.http.path === path).map(route => route.http.method)];
    }));

    const notFoundHandler = async (request: Http.Request) => {
      if (typeof request.headers.accept === 'string') {
        const accept = request.headers.accept.split(',');
        if (accept.includes('text/html')) {
          return {
            status: 404,
            body: new Blob([
              `
              <html>
                <body>
                  <h1>Not Found</h1>
                  <p>${request.method}:\t${request.path}<p/>
                  <pre>${httpRoutes.map(r => `${r.http.method}:\t${r.http.path}`).join('\n')}</pre>
                </body>
              </html>
              `
            ]).stream(),
            headers: { 'content-type': 'text/html' },
          }
        }
      }

      return { status: 404, body: null, headers: {} };
    }

    const createErrorHandler = (inner: Http.Handler): Http.Handler => async (request) => {
      try {
        return await inner(request)
      } catch (error) {
        console.warn('Error converted to 500:', error);

        if (typeof request.headers.accept === 'string' && error instanceof Error) {
          const accept = request.headers.accept.split(',');
          if (accept.includes('text/html')) {
            return {
              status: 404,
              body: new Blob([
                `
                <html>
                  <body>
                    <h1>Server Error</h1>
                    <p>${request.method}:\t${request.path}<p/>
                    <pre>${error.message}</pre>
                  </body>
                </html>
                `
              ]).stream(),
              headers: { 'content-type': 'text/html' },
            }
          }
        }
        return { status: 500, body: null, headers: {} };
      }
    }

    const createMetadataHandler = (inner: Http.Handler): Http.Handler => async (request) => {
      const origin = typeof request.headers.origin === 'string' && request.headers.origin;
      const methodsForPath = methodsByPath.get(request.path);
      const endpoint = endpointByPath.get(request.path);

      const allowHeaders: string[] = ['content-type', 'authorization'];

      if (endpoint) {
        switch (endpoint.type) {
          case 'json': {
            allowHeaders.push(...endpoint.cors.allowHeaders);
            break;
          }
        }
      }

      const CORS_HEADERS = {
        'Access-Control-Allow-Origin': '*',//origin || '',
        'Access-Control-Allow-Methods': methodsForPath && methodsForPath.join(', ') || '',
        'Access-Control-Allow-Headers': allowHeaders.join(', '),
      }
      
      if (request.method === 'OPTIONS') {
      
        return {
          status: 200,
          body: null,
          headers: CORS_HEADERS
        }
      }
      const response = await inner(request);

      return {
        ...response,
        headers: {
          ...response.headers,
          ...CORS_HEADERS,
        }
      }
    }

    return {
      http: httpRoutes.length > 0 ? createErrorHandler(createMetadataHandler(Http.createRoutesHandler(httpRoutes.map(h => h.http), notFoundHandler))) : null,
      ws: wsRoutes.length > 0 ? WebSocket.createRoutesHandler(wsRoutes, async (c) => c.disconnect()) : null,
      wsProtocols: WebSocket.createProtocolHandler(wsRoutes),
    } as any;
  },

  createClientFromURLMap<T extends HttpApi.Def | HttpApi.ApiNode>(
    def: T,
    map: HttpApi.URLMap<T>,
    net: NetworkClient
  ): HttpApi.Handler<"client", T> {
    type EndpointMap = HttpApi.URLMap<HttpEndpoint>;
    type BranchMap = HttpApi.URLMap<HttpApi.Branch>;

    switch (def.type) {
      case 'api':
      case 'branch':
        return Object.fromEntries(Object.keys(def.paths).map(path => {
          const pathDef = def.paths[path];
          const pathMap = (map as BranchMap)[path];

          return [path, HttpApi.createClientFromURLMap(pathDef, pathMap, net)] as const;
        })) as HttpApi.Handler<"client", T>
      case 'json':
        const jsonURL = map as EndpointMap;
        return HttpEndpoint.Clients.Json(
          jsonURL,
          def,
          net.http
        ) as HttpApi.Handler<"client", T>;
      case 'methods':
        const methodsURL = map as EndpointMap;
        return Object.fromEntries(Object.entries(def.methods).map(([method, methodDef]) => {
          return [method, Endpoint.client(methodDef, methodsURL, method, net.http)]
        })) as HttpApi.Handler<"client", T>;
      case 'raw':
        const rawURL = map as EndpointMap;
        return HttpEndpoint.Clients.Raw(
          rawURL,
          net.http
        ) as HttpApi.Handler<"client", T>;
      case 'websocket':
        const websocketURL = map as EndpointMap;
        return HttpEndpoint.Clients.WebSocket(
          websocketURL,
          def,
          net.ws
        ) as HttpApi.Handler<"client", T>;
      case 'rest':
      default:
        throw new Error();
    }
  },

  createClient<T extends HttpApi.Def>(
    def: T,
    baseURL: string,
    net: NetworkClient
  ): HttpApi.Handler<"client", T> {
    const createClientForEndpoint = (
      pathParts: string[],
      node: HttpApi.ApiNode
    ): HttpApi.Handler<"client">  => {
      switch (node.type) {
        case 'branch':
          return createClientForPaths(pathParts, node.paths);
        case 'websocket':
          const wsUrl = new URL([baseURL, ...pathParts].join('/'));
          return HttpEndpoint.Clients.WebSocket(wsUrl, node, net.ws)
        case 'raw':
          const rawUrl = new URL([baseURL, ...pathParts].join('/'));
          return HttpEndpoint.Clients.Raw(rawUrl, net.http);
        case 'json':
          const jsonURL = new URL([baseURL, ...pathParts].join('/'));
          return HttpEndpoint.Clients.Json(jsonURL, node, net.http);
        case 'methods': {
          const url = new URL([baseURL, ...pathParts].join('/'));

          return Object.fromEntries(Object.entries(node.methods).map(([method, methodDef]) => {
            return [method, Endpoint.client(methodDef, url, method, net.http)]
          }));
        }
        default:
          throw new Error(`Unimplemented`)
      }
    }

    const createClientForPaths = (parentParts: string[], paths: HttpApi.PathMap): HttpApi.Handler<"client"> => {
      return Object.fromEntries(
        Object.entries(paths)
          .map(([path, value]) => {
            const pathParts = [...parentParts, ...path.split('/')]
              .filter(Boolean);

            return [path, createClientForEndpoint(pathParts, value)];
          })
      );
    }

    return createClientForPaths([], def.paths) as HttpApi.Handler<"client", T>;
  },

  createURLMap<T extends HttpApi.ApiNode | HttpApi>(def: T, baseURL: URL): HttpApi.URLMap<T> {
    switch (def.type) {
      case 'branch':
      case 'api':
        return Object.fromEntries(Object.entries(def.paths).map(([pathSegment, apiNode]) => {
          const segments = [...baseURL.pathname.split('/'), ...pathSegment.split('/')].filter(Boolean)
          const pathURL = new URL('/' + segments.join('/'), baseURL)
          return [pathSegment, HttpApi.createURLMap(apiNode, pathURL)]
        })) as HttpApi.URLMap<T>;
      case 'json':
      case 'methods':
      case 'raw':
      case 'rest':
      case 'websocket':
        return baseURL as HttpApi.URLMap<T>;
    }
  }
};

